import { get, post, put } from './api';
import { getAttribution } from '@quanty-labs/facilita-tracking-package';

import datetimeProvider from '../../providers/datetime.provider';
import serviceWorker from "./service.worker.service";
import LocalStorageProvider from "../../components/providers/local.storage.provider";

const storageNames = [
    { name: '@facilita-app/user', keyData: 'user', isJson: true },
    { name: '@facilita-app/token', keyData: 'token', isJson: false },
];

const getUserAuthentication = () => JSON.parse(localStorage.getItem('@facilita-app/user'));

const checkCurrentUserSource = (sourceName) => (getUserAuthentication()?.source === sourceName);

const isMainCustomer = () =>
    checkCurrentUserSource('customer');

const isCustomer = () =>
    checkCurrentUserSource('customer') || checkCurrentUserSource('customerUser');

const isCustomerAdmin = () =>
    isValidRoles(["customer", "customer-user-admin"], getUserAuthentication());

const isTestUser = () =>
    isValidRoles(['test'], getUserAuthentication());

const isFaciliter = () => checkCurrentUserSource('faciliter');

const isFaciliterReviewer = () => isFaciliter() && (getUserAuthentication()?.faciliter?.isReviewer) === true

const isInvoiceOnlyFaciliter = () => isFaciliter() && !!getUserAuthentication()?.faciliter?.invoiceOnlyAccess

const isExternal = () => isFaciliter() || isCustomer();

const isInternal = () => checkCurrentUserSource('internal');

const signIn = (user) =>
    post({ type: 'user', service: 'users/authenticate', data: user })
        .then(results => {
            setUserAuthentication(results.data);
            redirectLastAccess(results.data);

            LocalStorageProvider.refreshStorage(
                LocalStorageProvider.storages.general.name,
                prev => ({ ...prev, lastAccess: datetimeProvider.formatDateTime() })
            );

            return results;
        });

const signInWithUUID = (uuid, ref = 'email') =>
    get({ type: "user", service: `users/authenticate/${uuid}?ref=${ref}` })
        .then(results => {
            setUserAuthentication(results.data);
            return results;
        });

const signInLead = (email) =>
    post({ type: 'user', service: 'users/authenticate-lead', data: { email } })
        .then(results => {
            setUserAuthentication(results.data);
            return results;
        });

const resetPassword = (data) =>
    put({ type: 'user', service: 'users/new-password', data });

const updatePassword = (data) =>
    put({ type: 'user', service: 'users/refresh/password', data });

const signOut = () => {

    try {
        storageNames.forEach(x => localStorage.removeItem(x.name));
    } catch (error) {
        console.error(error);
    }
    serviceWorker.userLogout();
}

const setUserAuthentication = (data) => {
    const failures = [];
    storageNames.forEach((x) => {
        const value = x.isJson ? JSON.stringify(data[x.keyData]) : data[x.keyData];
        try {
            localStorage.setItem(x.name, value);
            const stored = localStorage.getItem(x.name);
            if (stored !== value)
                failures.push({ name: x.name, reason: 'read-after-write-mismatch' });
        } catch (err) {
            failures.push({ name: x.name, reason: 'setItem-threw', errorName: err?.name, errorMessage: err?.message });
        }
    });
    if (failures.length > 0)
        console.error('[reset-password] setUserAuthentication:storage-failed', {
            failures,
            userAgent: navigator.userAgent,
        });
    serviceWorker.setAuthenticatedUser()
}

const refreshUserAuthentication = (data) => {
    if (typeof data === 'function') {
        const newUserData = data(getUserAuthentication());
        localStorage.setItem('@facilita-app/user', JSON.stringify(newUserData))
        return;
    }

    localStorage.setItem('@facilita-app/user', JSON.stringify(data));
}


const redirectLogin = (email, password, redirectTo = "/") => {
    signIn({ email, password }).then((res) => setUserAuthentication(res.data))
        .finally(() => {
            window.location.href = redirectTo;
        })
}

const REDIRECT_AFTER_LOGIN_KEY = '@facilita-app/redirect-after-login';
const REDIRECT_AFTER_LOGIN_TTL_MS = 24 * 60 * 60 * 1000;

// '//' e '/\' o navegador lê como outro domínio
const isAppReturnPath = (url) =>
    typeof url === 'string'
    && url.startsWith('/')
    && !url.startsWith('//')
    && !url.startsWith('/\\')
    && !url.startsWith('/login');

const saveRedirectAfterLogin = (url) => {
    if (!isAppReturnPath(url))
        return;

    localStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, JSON.stringify({ url, savedAt: Date.now() }));
}

// Antes da validade, o destino era gravado como texto puro
const readRedirectAfterLogin = () => {
    const stored = localStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
    try {
        return JSON.parse(stored) || {};
    } catch (error) {
        return { url: stored };
    }
}

const isExpiredRedirect = (savedAt) =>
    !!savedAt && Date.now() - savedAt > REDIRECT_AFTER_LOGIN_TTL_MS;

const getRedirectAfterLogin = () => {
    const { url, savedAt } = readRedirectAfterLogin();

    return isAppReturnPath(url) && !isExpiredRedirect(savedAt) ? url : null;
}

const clearRedirectAfterLogin = () =>
    localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);

const isLogin = () =>
    localStorage.getItem('@facilita-app/token') ? true : false;

const getToken = () =>
    localStorage.getItem('@facilita-app/token');

const isValidRoles = (roles, currentUser) => {

    const user = currentUser || getUserAuthentication();

    if (!user) return false;

    return (!roles)
        ? true
        : roles.some((routeRole) => user.roles.some(userRole => userRole.name === routeRole));
}

const hasPermissionRole = (role, currentUser) =>
    (currentUser?.roles || []).some(item => item.name === role);

const getNameInitials = (name) => {
    if (!name)
        return null;

    return (name.match(/\b\w/g) || '').toString().toUpperCase().replace(',', ' ').substring(0, 3);
}

const saveAccess = (userId) => {

    if (!userId)
        return

    const data = {
        userId: userId,
        url: window.location.href,
        datetime: new Date()
    }

    localStorage.setItem('@facilita-app/lastAccess', JSON.stringify(data));
}

const redirectLastAccess = ({ user }) => {

    const lastAccess = JSON.parse(localStorage.getItem('@facilita-app/lastAccess'));

    if (!lastAccess)
        return;

    localStorage.removeItem('@facilita-app/lastAccess');

    const redirectUser = user.id == lastAccess.userId && isValidRedirectionTime(lastAccess.datetime);

    if (!redirectUser)
        return;

    window.location.href = lastAccess.url
}

const isValidRedirectionTime = (lastAccessDatetime) => {
    const validRedirectionTime = 5;
    return datetimeProvider.getTimeDifference('minutes', lastAccessDatetime) <= validRedirectionTime;
}

const isFinancial = () => {
    return isValidRoles(["financial"]);
}

const getUTM = () => getAttribution();

const setCustomerViewUpgradeToInfinity = () => {
    localStorage.setItem('@facilita-app/viewUpgradeToInfinity', 'true');
}

const getCustomerViewUpgradeToInfinity = () => {
    return localStorage.getItem('@facilita-app/viewUpgradeToInfinity');
}

export default {
    checkCurrentUserSource,
    isInternal,
    isExternal,
    isMainCustomer,
    isCustomer,
    isCustomerAdmin,
    isFaciliter,
    isFaciliterReviewer,
    isInvoiceOnlyFaciliter,
    isFinancial,
    signIn,
    signInWithUUID,
    signInLead,
    resetPassword,
    updatePassword,
    signOut,
    setUserAuthentication,
    refreshUserAuthentication,
    isLogin,
    getUserAuthentication,
    getToken,
    isValidRoles,
    hasPermissionRole,
    redirectLogin,
    getNameInitials,
    saveAccess,
    getUTM,
    isTestUser,
    setCustomerViewUpgradeToInfinity,
    getCustomerViewUpgradeToInfinity,
    saveRedirectAfterLogin,
    getRedirectAfterLogin,
    clearRedirectAfterLogin
}